import Collapse from '@mui/material/Collapse';
import { useEffect, useState } from 'react';

import { type BoxProps, Box } from '../../box';
import { type IconButtonProps, IconButton } from '../../button';
import { ExpandMoreRoundedIcon } from '../../icons';
import { styled } from '../../theme';
import { DragDropContext } from '../components';
import { type DraggableOptionProps, Draggable, DraggableBehavior } from '../components/Draggable';
import { type DroppableProps, Droppable } from '../components/Droppable';
import { reorder, reorderNestedChildren, findItemById } from '../utils/reorderFunctions';
import type { RenderItem as BaseRenderItem, DraggableLocation, DropResult } from '../utils/types';

interface DroppableStyleProps extends BoxProps {
  direction?: DroppableProps['direction'];
}

type DndListProps = {
  draggableProps?: Omit<BoxProps, 'ref' | 'color'>;
} & Omit<DraggableOptionProps, 'behavior'> &
  DroppableStyleProps;

export interface RenderItem<P> extends BaseRenderItem<P> {
  listProps?: DndListProps;
  collapsible?: boolean;
}

interface OnDragEndParams<P> {
  updatedItem: RenderItem<P> | null;
  from: { item: RenderItem<P> | null; index: number };
  to: { item: RenderItem<P> | null; index: number };
  data: RenderItem<P>[];
}

interface MultipleNestedDndListProps<P> {
  data: P[];
  mapRenderItem: (item: P, index?: number) => RenderItem<P>;
  onDragEnd?: (arg: OnDragEndParams<P>) => void;
  withExpandButton?: boolean;
  behavior: DraggableOptionProps['behavior'];
}

const ExpandIcon = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<IconButtonProps & { expanded: boolean }>(({ expanded }) => ({
  padding: 0,
  marginLeft: 'auto',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'all 100ms ease-in-out',
}));

function toggle(itemId: string, setCollapsedIds: React.Dispatch<React.SetStateAction<string[]>>) {
  setCollapsedIds((prev) => {
    return prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId];
  });
}

function handleReorder<P>(
  result: DropResult,
  setOrder: React.Dispatch<React.SetStateAction<RenderItem<P>[]>>,
  onDragEnd?: (arg: OnDragEndParams<P>) => void,
) {
  // dropped nowhere
  if (!result.destination) {
    return;
  }
  const source: DraggableLocation = result.source;
  const destination: DraggableLocation = result.destination;
  // did not move anywhere - can bail early
  if (source.droppableId === destination.droppableId && source.index === destination.index) {
    return;
  }

  setOrder((prev) => {
    let prevState: typeof prev = [...prev];
    // reordering parent
    if (result.type === 'PARENT') {
      prevState = reorder(prevState, source.index, destination.index);
    } else {
      const reordered = reorderNestedChildren({
        order: prevState,
        source,
        destination,
      });
      prevState = reordered;
    }

    const sourceItem = findItemById(prevState, source.droppableId);
    const destinationItem = findItemById(prevState, destination.droppableId);
    const updatedItem = findItemById(prevState, result?.draggableId);

    onDragEnd?.({
      updatedItem,
      from: { item: sourceItem, index: source.index },
      to: { item: destinationItem, index: destination.index },
      data: prevState,
    });

    return prevState;
  });
}

function MultipleNestedDndList<P>(props: Readonly<MultipleNestedDndListProps<P>>) {
  const { data, mapRenderItem, onDragEnd, withExpandButton, behavior } = props;

  // TODO: Recheck whether it is possible to remove this state and use data directly
  const [order, setOrder] = useState<RenderItem<P>[]>([]);
  const [collapsedIds, setCollapsedIds] = useState<string[]>([]);

  // Previously, It's useDidUpdate
  // But might not necessary to use useDidUpdate and replaced with useEffect
  useEffect(() => {
    setOrder(data.map(mapRenderItem));
  }, [data]);

  const renderDraggableItem = (item: RenderItem<P>, index: number, depth = 0) => {
    const { listProps, collapsible: isCollapsible } = item;
    const {
      color: listColor,
      variant: listVariant,
      disableGutters: isListDisableGutters,
      draggableProps: listDraggableProps,
      ...restListProps
    } = listProps ?? {};

    const isCollapsed = collapsedIds.includes(item.id);
    const hasChildren = (item.children ?? []).length > 0;
    const shouldHideChildren = isCollapsible && isCollapsed && hasChildren;

    const shouldCollapse = isCollapsible && !withExpandButton;

    return (
      <Draggable
        key={item.id}
        {...listDraggableProps}
        behavior={behavior}
        draggableId={item.id}
        index={index}
        color={listColor}
        variant={listVariant}
        disableGutters={isListDisableGutters}
        onClick={shouldCollapse ? () => toggle(item.id, setCollapsedIds) : undefined}
        sx={
          shouldCollapse
            ? {
                ...listDraggableProps?.sx,
                '> .MuiBox-root:first-of-type': {
                  '&:hover': {
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                  },
                },
              }
            : listDraggableProps?.sx
        }
        nestedEl={
          <Box sx={{ pl: 4 }} onClick={(e) => e.stopPropagation()}>
            <Collapse in={!shouldHideChildren}>
              <Droppable
                droppableId={item.id}
                type={'CHILDREN' + '-' + depth}
                rbdProps={{
                  isDropDisabled: shouldHideChildren,
                }}
                {...restListProps}
              >
                {(item.children ?? []).map((child, childIndex) =>
                  renderDraggableItem(child, childIndex, depth + 1),
                )}
              </Droppable>
              {item.staticEndEl}
            </Collapse>
          </Box>
        }
      >
        {item.content}
        {isCollapsible ? (
          <ExpandIcon
            expanded={!isCollapsed}
            disableRipple={!withExpandButton}
            onClick={withExpandButton ? () => toggle(item.id, setCollapsedIds) : undefined}
          >
            <ExpandMoreRoundedIcon />
          </ExpandIcon>
        ) : null}
      </Draggable>
    );
  };

  return (
    <DragDropContext onDragEnd={(result) => handleReorder(result, setOrder, onDragEnd)}>
      <Droppable droppableId='multiple-dnd-list' type='PARENT'>
        {order.map((item, index) => renderDraggableItem(item, index))}
      </Droppable>
    </DragDropContext>
  );
}

export { DraggableBehavior as DragDropBehavior };
export type { MultipleNestedDndListProps };
export default MultipleNestedDndList;
